import React, { useState, useEffect } from 'react';
import { useAuth } from '../../components/Auth/AuthContext';
import { useApp } from '../../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Camera, Heart, MapPin, List, Star, Upload, Edit2, ArrowLeft, Home } from 'lucide-react';
import UserProfileHeader from './UserProfileHeader';
import LikedSpotsSection from './LikedSpotsSection';
import VisitedSpotsSection from './VisitedSpotsSection';
import WishlistsSection from './WishlistsSection';
import ContributionsSection from './ContributionsSection';
import LikedPhotosSection from './LikedPhotosSection';
import SpotDetailsModal from '../../components/Modals/SpotDetailsModal';

export default function ProfilePage() {
  const { user, isAuthenticated, loading } = useAuth();
  const { state, actions } = useApp();
  const navigate = useNavigate();
  const [authCheckDelay, setAuthCheckDelay] = useState(true);

  // Give some time for auth to be restored from localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      setAuthCheckDelay(false);
    }, 1000); // Wait 1 second before checking auth status
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!loading && !authCheckDelay && !isAuthenticated) {
      console.log('ProfilePage: Redirecting due to no authentication');
      navigate('/');
    }
  }, [isAuthenticated, loading, navigate, authCheckDelay]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Chargement...</div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-6">
        {/* Navigation Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="flex items-center gap-2 hover:bg-blue-50 hover:border-blue-300"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour aux spots
          </Button>
          
          <div className="flex items-center gap-2 text-gray-600">
            <Home className="h-4 w-4" />
            <span className="text-sm">Mon profil</span>
          </div>
        </div>

        {/* Profile Header */}
        <UserProfileHeader user={user} />

        {/* Profile Tabs */}
        <Tabs defaultValue="liked-spots" className="mt-8">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="liked-spots" className="flex items-center gap-2">
              <Heart className="h-4 w-4" />
              Spots likés
            </TabsTrigger>
            <TabsTrigger value="visited-spots" className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Mes visites
            </TabsTrigger>
            <TabsTrigger value="wishlists" className="flex items-center gap-2">
              <List className="h-4 w-4" />
              Listes d'envie
            </TabsTrigger>
            <TabsTrigger value="contributions" className="flex items-center gap-2">
              <Star className="h-4 w-4" />
              Contributions
            </TabsTrigger>
            <TabsTrigger value="liked-photos" className="flex items-center gap-2">
              <Camera className="h-4 w-4" />
              Photos likées
            </TabsTrigger>
            <TabsTrigger value="my-photos" className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Mes photos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="liked-spots">
            <LikedSpotsSection />
          </TabsContent>

          <TabsContent value="visited-spots">
            <VisitedSpotsSection />
          </TabsContent>

          <TabsContent value="wishlists">
            <WishlistsSection />
          </TabsContent>

          <TabsContent value="contributions">
            <ContributionsSection />
          </TabsContent>

          <TabsContent value="liked-photos">
            <LikedPhotosSection />
          </TabsContent>

          <TabsContent value="my-photos">
            <div className="text-center py-12">
              <Camera className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-600">Mes photos publiées</h3>
              <p className="text-gray-500">Fonctionnalité en développement</p>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Spot Details Modal */}
      {state.showSpotDetails && state.selectedSpot && (
        <SpotDetailsModal
          onClose={() => actions.setShowSpotDetails(false)}
        />
      )}
    </div>
  );
}